import React from 'react';
import type { FilterItem } from '../../models/search-result';
import {
  getDefaultExpandedState,
  type SidebarCategoryNode,
} from './FilterSidebar.helpers';
import FilterSection from './FilterSection';
import type {
  FilterSidebarExpandedState,
  FilterSidebarShowMoreState,
} from './useFilterSidebarState';

interface FilterCategoryNodeProps {
  node: SidebarCategoryNode;
  expandedSections: FilterSidebarExpandedState;
  showMoreStates: FilterSidebarShowMoreState;
  valueCategoriesCollapsedByDefault: boolean;
  onToggleSection: (section: string) => void;
  onToggleShowMore: (section: string) => void;
  onFilterToggle: (sectionId: string, filterItem: FilterItem) => void;
  isFilterChecked: (sectionId: string, filterItem: FilterItem) => boolean;
}

const FilterCategoryNode: React.FC<FilterCategoryNodeProps> = ({
  node,
  expandedSections,
  showMoreStates,
  valueCategoriesCollapsedByDefault,
  onToggleSection,
  onToggleShowMore,
  onFilterToggle,
  isFilterChecked,
}) => {
  const sectionProps = {
    expandedSections,
    showMoreStates,
    valueCategoriesCollapsedByDefault,
    onToggleSection,
    onToggleShowMore,
    onFilterToggle,
    isFilterChecked,
  };

  if (!node.label) {
    return (
      <>
        {node.sections.map((section) => (
          <FilterSection key={section.id} section={section} {...sectionProps} />
        ))}
      </>
    );
  }

  const categoryStateKey = `category:${node.id}`;
  const isExpanded =
    expandedSections[categoryStateKey] ??
    getDefaultExpandedState(
      categoryStateKey,
      valueCategoriesCollapsedByDefault,
    );

  return (
    <div key={node.id} className="filter-category-group">
      <button
        type="button"
        className="filter-category-header"
        onClick={() => onToggleSection(categoryStateKey)}
      >
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          ▶
        </span>
        <span className="filter-category-title">{node.label}</span>
        <span className="filter-category-count">
          ({node.count.toLocaleString()})
        </span>
      </button>
      {isExpanded && (
        <div className="filter-category-content">
          {node.sections.map((section) => (
            <FilterSection
              key={section.id}
              section={section}
              {...sectionProps}
            />
          ))}
          {node.children.map((childNode) => (
            <FilterCategoryNode
              key={childNode.id}
              node={childNode}
              {...sectionProps}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterCategoryNode;
